/* eslint-disable @typescript-eslint/naming-convention */
// deps
import { useMemo } from "react";

// internal deps
import { IRemovePlanMessage, RenderingDetails, SavingsLineItemData } from "@swiggy-private/cart-types";
import {
    COUPON_SAVINGS_LINE_ITEMS,
    SUPER_SAVINGS_LINE_ITEMS,
    VALID_RENDERING_DETAILS_TYPE,
    SAVINGS_BENEFITS_LINE_ITEMS,
    DELIVERY_FEE_AND_EXTRA_SAVINGS_LINE_ITEMS,
} from "@swiggy-private/cart-ui";

// helpers
import { filterRenderingDetails } from "@cart-client/helpers/CartRendering";

// hooks
import { useCouponSavings } from "./useCouponSavings";
import useSubscriptionSavings from "./useSubscriptionSavings";
import { useHabitFormingData } from "./useHabitFormingData";
import { useBenefitsData } from "./useBenefitsData";
import { useDeliveryFeeAndExtraSavings } from "./useDeliveryFeeAndExtraSavings";
export interface SavingsSectionProps {
    renderingDetails: RenderingDetails[];
    hideSectionTitle: boolean;
}

export interface UseSavingsSectionRendererReturnType {
    savingsLineItems: SavingsLineItemData[];
    removePlanMessage: IRemovePlanMessage;
}

export const useSavingsSectionRenderer = ({
    renderingDetails,
}: SavingsSectionProps): UseSavingsSectionRendererReturnType => {
    // Coupon line items
    const couponSavingsRenderingDetails = useMemo(
        () => filterRenderingDetails(renderingDetails, COUPON_SAVINGS_LINE_ITEMS),
        [renderingDetails],
    );

    const subscriptionSavingsRenderingDetails = useMemo(
        () => filterRenderingDetails(renderingDetails, SUPER_SAVINGS_LINE_ITEMS),
        [renderingDetails],
    );

    const savingsBenefitsRenderingDetails = useMemo(
        () => filterRenderingDetails(renderingDetails, SAVINGS_BENEFITS_LINE_ITEMS),
        [renderingDetails],
    );

    const deliveryFeeAndExtraSavingsRenderingDetails = useMemo(
        () => filterRenderingDetails(renderingDetails, DELIVERY_FEE_AND_EXTRA_SAVINGS_LINE_ITEMS),
        [renderingDetails],
    );

    const { getSubscriptionSavingsLineItems, removePlanMessage } = useSubscriptionSavings(
        subscriptionSavingsRenderingDetails,
    );

    const { getCouponSavingsLineItems } = useCouponSavings({
        couponSavingsRenderingDetails,
    });

    const { deliveryFeeAndExtraSavingsLineItems } = useDeliveryFeeAndExtraSavings({
        deliveryFeeAndExtraSavingsRenderingDetails,
    });

    // Habit forming line items
    const habitFormingRenderingDetails = useMemo(
        () => filterRenderingDetails(renderingDetails, [VALID_RENDERING_DETAILS_TYPE.HABIT_FORMATION]),
        [renderingDetails],
    );

    const { getHabitFormingLineItems } = useHabitFormingData(habitFormingRenderingDetails[0]);

    const { getSavingsBenefitsItem } = useBenefitsData({
        savingsBenefitsRenderingDetails: savingsBenefitsRenderingDetails[0],
    });

    // Collating all savings line items
    const savingsLineItems: SavingsLineItemData[] = useMemo(() => {
        const couponSavingsLineItems = getCouponSavingsLineItems();
        const subscriptionLineItems = getSubscriptionSavingsLineItems();
        const habitFormingLineItems = getHabitFormingLineItems();
        const savingBenefits = getSavingsBenefitsItem();

        return [
            ...couponSavingsLineItems,
            ...deliveryFeeAndExtraSavingsLineItems,
            ...subscriptionLineItems,
            ...habitFormingLineItems,
            ...savingBenefits,
        ];
    }, [
        deliveryFeeAndExtraSavingsLineItems,
        getCouponSavingsLineItems,
        getHabitFormingLineItems,
        getSavingsBenefitsItem,
        getSubscriptionSavingsLineItems,
    ]);

    return { savingsLineItems, removePlanMessage };
};
